import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowDownLeft, Loader2, CheckCircle2, Copy, ExternalLink } from 'lucide-react';
import { useDisplayUnit } from '@/hooks/useDisplayUnit';
import { useDepositFlow, type DepositState } from '@/hooks/useDepositFlow';
import { AmountInput } from '@/components/shared/AmountInput';
import { formatXmr, parseUserInput } from '@/lib/monero/units';
import { useToast } from '@/hooks/useToast';
import type { WalletMintEntry } from '@/lib/storage/db';
import { QRCodeCanvas } from '@/components/ui/qrcode';

interface DepositDialogProps {
  mint: WalletMintEntry;
  userPrivkey: string;
  fiatPrice?: number | null;
}

export function DepositDialog({ mint, userPrivkey, fiatPrice }: DepositDialogProps) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const { unit, toggle } = useDisplayUnit();
  const { state, startDeposit, reset } = useDepositFlow();
  const { toast } = useToast();

  const handleStart = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: 'Invalid amount', variant: 'destructive' });
      return;
    }
    const piconero = parseUserInput(amount, unit);
    if (piconero <= 0) {
      toast({ title: 'Invalid amount', variant: 'destructive' });
      return;
    }
    await startDeposit(piconero, mint.npub, mint.keysets, userPrivkey);
  };

  const handleOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) reset();
  };

  const copyAddress = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!', description: 'Address copied to clipboard.' });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="default">
          <ArrowDownLeft className="h-4 w-4 mr-1" />
          Deposit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Deposit to {mint.name}</DialogTitle>
          <DialogDescription>
            Deposit XMR to receive ecash tokens from this mint.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <DepositStep state={state} />

          {state.step === 'idle' && (
            <>
              <AmountInput
                value={amount}
                onChange={setAmount}
                unit={unit}
                onUnitToggle={toggle}
                label="Amount to deposit"
                fiatPrice={fiatPrice}
              />
              <Button onClick={handleStart} className="w-full">
                <ArrowDownLeft className="h-4 w-4 mr-2" />
                Request Deposit Address
              </Button>
            </>
          )}

          {state.step === 'preparing' || state.step === 'requesting' || state.step === 'awaiting_address' ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                {state.step === 'preparing' ? 'Preparing...' :
                 state.step === 'requesting' ? 'Requesting deposit address...' :
                 'Waiting for mint response...'}
              </span>
            </div>
          ) : state.step === 'address_ready' ? (
            <div className="space-y-4">
              <p className="text-sm font-medium">Send XMR to this address:</p>
              <div className="flex justify-center">
                  <QRCodeCanvas value={state.depositAddress} size={200} />
              </div>
              <div className="flex gap-2">
                <Input value={state.depositAddress} readOnly className="text-xs font-mono" />
                <Button size="icon" variant="outline" onClick={() => copyAddress(state.depositAddress)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Waiting for deposit... (may take 2+ minutes for Monero confirmation)
              </p>
              <Button variant="secondary" size="sm" className="w-full" onClick={handleStart} disabled>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Awaiting deposit...
              </Button>
            </div>
          ) : state.step === 'awaiting_deposit' || state.step === 'confirming' ? (
            <div className="space-y-4 text-center py-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-sm">
                {state.step === 'awaiting_deposit'
                  ? 'Deposit detected! Awaiting confirmation...'
                  : `Confirming block (${state.confirmations}/${state.required})...`}
              </p>
              {state.step === 'confirming' && state.txHash && (
                <p className="text-xs text-muted-foreground font-mono break-all">
                  TX: {state.txHash.slice(0, 32)}...
                </p>
              )}
            </div>
          ) : state.step === 'issuing_tokens' ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2 text-sm">Mint is issuing your ecash tokens...</span>
            </div>
          ) : state.step === 'done' ? (
            <div className="space-y-4 text-center py-4">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
              <p className="font-semibold text-lg">
                {formatXmr(state.amount, 6)} XMR deposited!
              </p>
              <p className="text-sm text-muted-foreground">
                Ecash tokens added to your wallet.
              </p>
            </div>
          ) : state.step === 'error' ? (
            <div className="space-y-4">
              <p className="text-sm text-destructive">{state.error}</p>
              <Button onClick={() => { reset(); setAmount(''); }} variant="outline" className="w-full">
                Try Again
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DepositStep({ state }: { state: DepositState }) {
  const steps = [
    { label: 'Request address', done: state.step !== 'idle' && state.step !== 'preparing' },
    { label: 'Send XMR', done: state.step === 'address_ready' },
    { label: 'Confirm block', done: state.step === 'done' },
  ];
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${s.done ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
            {s.done ? '✓' : i + 1}
          </div>
          <span>{s.label}</span>
          {i < steps.length - 1 && <div className="w-4 h-px bg-border mx-1" />}
        </div>
      ))}
    </div>
  );
}
